import { AppInsightsPortalLogger } from '@harmony/pcportal-shell-library';
import { Snippet } from '@microsoft/applicationinsights-web';
import { EventGroupType } from '../../../Common/Constants/EventGroupType.Constants';
import { AppSessionContext } from '../SessionContext/AppSessionContext';
import { WindowRef } from '../../../Apps/Models/WindowRef';
import { TelemetryAppContext } from './Models/TelemetryAppContext';
import {
  ErrorProperties,
  EventProperties,
  InformationProperties,
  KeyValuePair,
  PerformanceProperties,
} from './Models/TelemetryEntry';

const supportWorkspaceId = 'ck2ic0y';
const TelemetrySource = 'SupportUIV2';

export default class TelemetryService {
  private static instance: TelemetryService;

  private appInsightsLogger: AppInsightsPortalLogger;
  private appSessionContext = AppSessionContext.getInstance();
  private appContext: TelemetryAppContext = {
    buildId: this.appSessionContext.BuildId,
    source: TelemetrySource,
    referrer: document.referrer,
    locale: this.appSessionContext.Locale,
    correlationId: this.appSessionContext.CorrelationId,
    sessionId: this.appSessionContext.SessionId,
    currentUrl: window.location.href,
    appLoadUrl: window.location.href,
    workspace: supportWorkspaceId,
  };

  public static getInstance = () => {
    if (!TelemetryService.instance) {
      TelemetryService.instance = new TelemetryService();
    }

    return TelemetryService.instance;
  };

  private constructor() {
    const instrumentationKey = this.appSessionContext.TelemetryConfig.instrumentationKey;
    // Disabling Ajax Tracking for the app insights.
    // We catch all the unhandled promises and exceptions since they have potential to break sections of page.
    // [TODO]: Move it to html, that can come once we parallize resource fetching.
    // As config needs to be coming at Build Time.
    const aiConfig: Snippet = {
      config: {
        instrumentationKey,
        disableAjaxTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        disableExceptionTracking: false,
      },
    };
    this.appInsightsLogger = new AppInsightsPortalLogger();
    this.appInsightsLogger.registerAiConfig(
      instrumentationKey,
      (window as WindowRef).Support.buildId ?? '1.0.0',
      this.appSessionContext.CorrelationId,
      aiConfig
    );
  }

  public traceInformation(message: string, customEvents: KeyValuePair = {}, severityLevel?: number) {
    this.updateTelemetryAppContext();

    const informationProperties: InformationProperties = {
      type: 'information',
      severityLevel: severityLevel ?? 3,
      data: JSON.stringify(customEvents),
      ...this.appContext,
    };

    this.appInsightsLogger.trackTrace({ message: message }, informationProperties);
  }

  public traceEvent(eventName: string, customEvents: KeyValuePair = {}, eventGroupType?: EventGroupType) {
    this.updateTelemetryAppContext();

    const eventProperties: EventProperties = {
      type: 'event',
      groupType: eventGroupType ?? EventGroupType.nonTracked,
      data: JSON.stringify(customEvents),
      ...this.appContext,
    };

    this.appInsightsLogger.trackEvent({ name: eventName }, eventProperties);
  }

  public traceError(error: string | Error, customEvents: KeyValuePair = {}, severityLevel?: number) {
    this.updateTelemetryAppContext();

    const errorProperties: ErrorProperties = {
      type: 'error',
      severityLevel: severityLevel || 3,
      data: JSON.stringify(customEvents),
      ...this.appContext,
    };

    let errorObject: Error;

    if (typeof error === 'string') {
      errorObject = new Error(error);
    } else {
      errorObject = error;
    }

    this.appInsightsLogger.trackException({ error: errorObject ?? '' }, errorProperties);
  }

  public tracePerformance(
    eventName: string,
    performanceValue: number,
    customEvents: KeyValuePair = {},
    eventGroupType?: EventGroupType
  ) {
    this.updateTelemetryAppContext();
    const performanceProperties: PerformanceProperties = {
      type: 'performance',
      duration: performanceValue ?? '',
      groupType: eventGroupType ?? EventGroupType.nonTracked,
      data: JSON.stringify(customEvents),
      ...this.appContext,
    };

    this.appInsightsLogger.trackEvent({ name: eventName }, performanceProperties);
  }

  private updateTelemetryAppContext() {
    (this.appContext as TelemetryAppContext).currentUrl = window.location.href;
  }
}
